import {
	getMapboxAccessToken,
	MAPBOX_GEOCODE_V6_FORWARD_URL,
	MAPBOX_GEOCODE_V6_REVERSE_URL,
} from "@/features/location-management/map/mapbox-constants";
import { isValidCoordinatePair } from "@/features/location-management/utils/shared/coordinates";

export type ReverseGeocodeAddress = {
	city?: string;
	postalCode?: string;
	stateProvince?: string;
	street1?: string;
};

type MapboxAddressContext = MapboxContextEntity & {
	address_number?: string;
	street_name?: string;
};

type MapboxAutofillFeatureProperties = MapboxGeocodeFeatureProperties & {
	address_level1?: string;
	address_level2?: string;
	address_line1?: string;
	feature_name?: string;
	postcode?: string;
};

type MapboxContextEntity = {
	name?: string;
	region_code?: string;
};

type MapboxGeocodeContext = {
	address?: MapboxAddressContext;
	place?: MapboxContextEntity;
	postcode?: MapboxContextEntity;
	region?: MapboxContextEntity;
};

type MapboxGeocodeFeature = {
	geometry?: {
		coordinates?: [number, number] | number[];
		type?: string;
	};
	properties?: MapboxGeocodeFeatureProperties;
};

type MapboxGeocodeFeatureProperties = {
	context?: MapboxGeocodeContext;
	name?: string;
};

type MapboxGeocodeResponse = {
	features?: MapboxGeocodeFeature[];
};

const joinStreet = (
	addressNumber: string | undefined,
	streetName: string | undefined,
): string | undefined => {
	const parts = [addressNumber, streetName]
		.map((part) => part?.trim())
		.filter((part): part is string => Boolean(part));

	return parts.length > 0 ? parts.join(" ") : undefined;
};

/** Maps a Mapbox Geocoding v6 feature into location form address fields. */
export const mapGeocodeFeatureToAddress = (
	feature: MapboxGeocodeFeature | undefined,
): ReverseGeocodeAddress | undefined => {
	if (!feature?.properties) return undefined;

	const { context, name } = feature.properties;
	const street1 =
		joinStreet(
			context?.address?.address_number,
			context?.address?.street_name,
		) ??
		name?.trim() ??
		undefined;

	const city = context?.place?.name?.trim() || undefined;
	const stateProvince =
		context?.region?.region_code?.trim() ||
		context?.region?.name?.trim() ||
		undefined;
	const postalCode = context?.postcode?.name?.trim() || undefined;

	if (!street1 && !city && !stateProvince && !postalCode) {
		return undefined;
	}

	return {
		city,
		postalCode,
		stateProvince,
		street1,
	};
};

/** Maps an Address Autofill retrieve feature into location form address fields. */
export const mapAutofillFeatureToAddress = (
	feature: MapboxGeocodeFeature | undefined,
): ReverseGeocodeAddress | undefined => {
	const fromContext = mapGeocodeFeatureToAddress(feature);
	if (fromContext) return fromContext;

	const properties = feature?.properties as
		| MapboxAutofillFeatureProperties
		| undefined;
	if (!properties) return undefined;

	const street1 =
		properties.address_line1?.trim() ||
		properties.feature_name?.trim() ||
		undefined;
	const city = properties.address_level2?.trim() || undefined;
	const stateProvince = properties.address_level1?.trim() || undefined;
	const postalCode = properties.postcode?.trim() || undefined;

	if (!street1 && !city && !stateProvince && !postalCode) {
		return undefined;
	}

	return {
		city,
		postalCode,
		stateProvince,
		street1,
	};
};

export const buildAddressQuery = (
	address: ReverseGeocodeAddress,
): string | undefined => {
	const parts = [
		address.street1,
		address.city,
		address.stateProvince,
		address.postalCode,
	]
		.map((part) => part?.trim())
		.filter((part): part is string => Boolean(part));

	return parts.length > 0 ? parts.join(", ") : undefined;
};

type ReverseGeocodeParams = {
	accessToken?: string;
	coordinates: [number, number];
	signal?: AbortSignal;
};

/**
 * Reverse-geocodes a lng/lat pair via Mapbox Geocoding v6.
 * Returns undefined when coordinates are invalid, the request fails, or no address is found.
 */
export const reverseGeocode = async ({
	accessToken = getMapboxAccessToken(),
	coordinates,
	signal,
}: ReverseGeocodeParams): Promise<ReverseGeocodeAddress | undefined> => {
	if (!isValidCoordinatePair(coordinates)) return undefined;
	if (!accessToken) {
		console.warn("reverseGeocode: missing Mapbox access token");
		return undefined;
	}

	const [lng, lat] = coordinates;

	try {
		const url = new URL(MAPBOX_GEOCODE_V6_REVERSE_URL);
		url.searchParams.set("longitude", String(lng));
		url.searchParams.set("latitude", String(lat));
		url.searchParams.set("types", "address");
		url.searchParams.set("access_token", accessToken);

		const fetchAddress = async (requestUrl: URL) => {
			const response = await fetch(requestUrl.toString(), { signal });
			if (!response.ok) {
				throw new Error(`Reverse geocode failed (${response.status})`);
			}

			const data = (await response.json()) as MapboxGeocodeResponse;
			return mapGeocodeFeatureToAddress(data.features?.[0]);
		};

		const address = await fetchAddress(url);
		if (address) return address;

		// Fall back without the address type filter when no street hit is returned
		url.searchParams.delete("types");
		return await fetchAddress(url);
	} catch (error) {
		if (signal?.aborted) return undefined;
		if (error instanceof DOMException && error.name === "AbortError") {
			return undefined;
		}
		console.warn("reverseGeocode: request failed", error);
		return undefined;
	}
};

type ForwardGeocodeParams = {
	accessToken?: string;
	query: string;
	signal?: AbortSignal;
};

type ForwardGeocodeResult = {
	address?: ReverseGeocodeAddress;
	coordinates: [number, number];
};

/**
 * Forward-geocodes a free-text address via Mapbox Geocoding v6.
 * Returns undefined when the query is empty, the request fails, or no match is found.
 */
export const forwardGeocode = async ({
	accessToken = getMapboxAccessToken(),
	query,
	signal,
}: ForwardGeocodeParams): Promise<ForwardGeocodeResult | undefined> => {
	const trimmedQuery = query.trim();
	if (!trimmedQuery) return undefined;
	if (!accessToken) {
		console.warn("forwardGeocode: missing Mapbox access token");
		return undefined;
	}

	try {
		const url = new URL(MAPBOX_GEOCODE_V6_FORWARD_URL);
		url.searchParams.set("q", trimmedQuery);
		url.searchParams.set("access_token", accessToken);

		const response = await fetch(url.toString(), { signal });
		if (!response.ok) {
			throw new Error(`Forward geocode failed (${response.status})`);
		}

		const data = (await response.json()) as MapboxGeocodeResponse;
		const feature = data.features?.[0];
		const coordinates =
			feature?.geometry?.type === "Point" &&
			isValidCoordinatePair(feature.geometry.coordinates)
				? feature.geometry.coordinates
				: undefined;

		if (!coordinates) return undefined;

		return {
			address: mapAutofillFeatureToAddress(feature),
			coordinates,
		};
	} catch (error) {
		if (signal?.aborted) return undefined;
		if (error instanceof DOMException && error.name === "AbortError") {
			return undefined;
		}
		console.warn("forwardGeocode: request failed", error);
		return undefined;
	}
};
